import React, { useState, useRef } from 'react';
import MobileTradingHeader from './MobileTradingHeader';
import MobileTradingForm from './MobileTradingForm';
import MobileBottomActionBar from './MobileBottomActionBar';
import TradingChart from '../TradingChart';
import OrderBook from '../OrderBook';
import RecentTrades from '../RecentTrades';
import PositionsPanel from '../PositionsPanel';
import { TradingMode, FuturesPosition } from '../../App';

type TabType = 'chart' | 'orderbook' | 'trades';

interface MobileTradingLayoutProps {
  selectedPair: string;
  tradingMode: 'futures' | 'cfd';
  onPairChange: (pair: string) => void;
  usdtBalance: number;
  availableBalance: number;
  maxAllowedLeverage: number;
  minAllowedLeverage?: number;
  calculateLiquidationPrice: (
    side: 'long' | 'short',
    entryPrice: number,
    leverage: number,
    marginType: 'isolated' | 'cross',
    amount?: number,
    totalBalance?: number
  ) => number;
  onTrade: (
    symbol: string,
    side: 'long' | 'short',
    amount: number,
    leverage: number,
    marginType: 'isolated' | 'cross',
    stopLoss?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number },
    takeProfit?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number },
    orderType?: 'market' | 'limit',
    price?: number
  ) => void;
  positions: FuturesPosition[];
  openOrders: any[];
  positionHistory: any[];
  orderHistory: any[];
  onClosePosition: (positionId: string, livePrice: number) => void;
  onCancelOrder: (orderId: string) => void;
  onCancelAllOrders: () => void;
  onEditTPSL?: (positionId: string, type: 'takeProfit' | 'stopLoss') => void;
}

const MobileTradingLayout: React.FC<MobileTradingLayoutProps> = ({
  selectedPair,
  tradingMode,
  onPairChange,
  usdtBalance,
  availableBalance,
  maxAllowedLeverage,
  minAllowedLeverage = 1,
  calculateLiquidationPrice,
  onTrade,
  positions,
  openOrders,
  positionHistory,
  orderHistory,
  onClosePosition,
  onCancelOrder,
  onCancelAllOrders,
  onEditTPSL
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('chart');
  const [showTradingForm, setShowTradingForm] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);

  const tabs: { key: TabType; label: string }[] = [
    { key: 'chart', label: 'Chart' },
    { key: 'orderbook', label: 'Order Book' },
    { key: 'trades', label: 'Trades' }
  ];

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    const swipeThreshold = 50;
    const diff = touchStartX.current - touchEndX.current;

    if (Math.abs(diff) > swipeThreshold) {
      const currentIndex = tabs.findIndex(t => t.key === activeTab);
      if (diff > 0 && currentIndex < tabs.length - 1) {
        setActiveTab(tabs[currentIndex + 1].key);
      } else if (diff < 0 && currentIndex > 0) {
        setActiveTab(tabs[currentIndex - 1].key);
      }
    }
  };

  const handleBuySellClick = () => {
    setShowTradingForm(true);
  };

  if (showTradingForm) {
    return (
      <div className="flex flex-col h-[calc(100vh-64px)] bg-white">
        <MobileTradingHeader
          selectedPair={selectedPair}
          tradingMode={tradingMode}
          onPairChange={onPairChange}
          onBackClick={() => setShowTradingForm(false)}
          showBackButton
        />

        <div className="flex border-b border-gray-200">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto">
          <MobileTradingForm
            selectedPair={selectedPair}
            usdtBalance={usdtBalance}
            availableBalance={availableBalance}
            maxAllowedLeverage={maxAllowedLeverage}
            minAllowedLeverage={minAllowedLeverage}
            calculateLiquidationPrice={calculateLiquidationPrice}
            onTrade={onTrade}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] bg-gray-50">
      <MobileTradingHeader
        selectedPair={selectedPair}
        tradingMode={tradingMode}
        onPairChange={onPairChange}
      />

      <div className="flex border-b border-gray-200 bg-white">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div
        ref={contentRef}
        className="flex-1 overflow-y-auto"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {activeTab === 'chart' && (
          <div className="flex flex-col">
            <div className="h-[calc(100vh-64px-140px-44px-64px)] min-h-[350px] bg-white">
              <TradingChart key={selectedPair} selectedPair={selectedPair} />
            </div>
            <div className="bg-white border-t border-gray-200">
              <PositionsPanel
                positions={positions}
                openOrders={openOrders}
                positionHistory={positionHistory}
                orderHistory={orderHistory}
                onClosePosition={onClosePosition}
                onCancelOrder={onCancelOrder}
                onCancelAllOrders={onCancelAllOrders}
                onEditTPSL={onEditTPSL}
              />
            </div>
          </div>
        )}

        {activeTab === 'orderbook' && (
          <div className="bg-white min-h-full">
            <OrderBook selectedPair={selectedPair} tradingMode={tradingMode as TradingMode} />
          </div>
        )}

        {activeTab === 'trades' && (
          <div className="bg-white min-h-full">
            <RecentTrades selectedPair={selectedPair} quoteCurrency={tradingMode === 'cfd' ? 'USD' : 'USDT'} />
          </div>
        )}
      </div>

      <MobileBottomActionBar onBuySellClick={handleBuySellClick} />
    </div>
  );
};

export default MobileTradingLayout;
